import { useState, useEffect } from 'react';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';

export default function SelfEmploymentTax() {
  useEffect(() => {
    document.title = "Self-Employment Tax Calculator 2025 | Calculate SE Tax - ItsJustTax";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Calculate your 2025 self-employment tax with our free SE tax calculator. Estimate Social Security and Medicare taxes for freelancers, contractors, and small business owners. Includes Schedule SE calculations and deductions.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'self-employment tax, SE tax calculator, Schedule SE, freelancer taxes, contractor tax calculator, small business tax');
    }
  }, []);

  const [netIncome, setNetIncome] = useState('');
  const [expenses, setExpenses] = useState('');
  const [result, setResult] = useState<any>(null);
  const [scrolled, setScrolled] = useState(false);

  const calculate = () => {
    const income = parseFloat(netIncome);
    const businessExpenses = parseFloat(expenses) || 0;

    if (isNaN(income) || income <= 0) {
      alert('Please enter a valid income amount');
      return;
    }

    const netBusinessIncome = income - businessExpenses;
    const selfEmploymentIncome = netBusinessIncome * 0.9235;
    const socialSecurityTax = Math.min(selfEmploymentIncome, 168600) * 0.124;
    const medicareTax = selfEmploymentIncome * 0.029;
    const totalSETax = socialSecurityTax + medicareTax;
    const deductibleSETax = totalSETax * 0.5;
    
    const taxableIncome = netBusinessIncome - deductibleSETax - 14600;
    const incomeTax = calculateIncomeTax(Math.max(0, taxableIncome));
    
    const totalTax = totalSETax + incomeTax;
    const netAfterTax = netBusinessIncome - totalTax;
    const effectiveRate = (totalTax / netBusinessIncome) * 100;

    const quarterlyPayment = totalTax / 4;

    setResult({
      grossIncome: income,
      expenses: businessExpenses,
      netBusinessIncome,
      socialSecurityTax,
      medicareTax,
      totalSETax,
      deductibleSETax,
      incomeTax,
      totalTax,
      netAfterTax,
      effectiveRate,
      quarterlyPayment
    });
  };

  const calculateIncomeTax = (taxableIncome: number) => {
    if (taxableIncome <= 11600) return taxableIncome * 0.10;
    if (taxableIncome <= 47150) return 1160 + (taxableIncome - 11600) * 0.12;
    if (taxableIncome <= 100525) return 5426 + (taxableIncome - 47150) * 0.22;
    if (taxableIncome <= 191950) return 17168.50 + (taxableIncome - 100525) * 0.24;
    if (taxableIncome <= 243725) return 39110.50 + (taxableIncome - 191950) * 0.32;
    if (taxableIncome <= 609350) return 55678.50 + (taxableIncome - 243725) * 0.35;
    return 183647.50 + (taxableIncome - 609350) * 0.37;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation scrolled={scrolled} />
      
      <div className="pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-8">
            <a href="/" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 mb-4 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </a>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Self-Employment Tax Calculator</h1>
            <p className="text-lg text-gray-600">Calculate your self-employment tax and quarterly estimated payments for 2026</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Business Income</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Gross Business Income</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={netIncome}
                      onChange={(e) => setNetIncome(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="100000"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Total revenue from your business</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Business Expenses</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={expenses}
                      onChange={(e) => setExpenses(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="20000"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Deductible business expenses</p>
                </div>

                <div className="bg-teal-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm">What is Self-Employment Tax?</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Self-employment tax covers Social Security (12.4%) and Medicare (2.9%) taxes. 
                    As a self-employed individual, you pay both the employer and employee portions, 
                    totaling 15.3% on 92.35% of your net earnings.
                  </p>
                </div>

                <button
                  onClick={calculate}
                  className="w-full py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl font-semibold hover:from-teal-700 hover:to-teal-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Calculate Tax
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 shadow-lg text-white">
              <h2 className="text-2xl font-bold mb-6">Tax Breakdown</h2>
              
              {result ? (
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Gross Income</span>
                      <span className="font-semibold">${result.grossIncome.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Business Expenses</span>
                      <span className="font-semibold">-${result.expenses.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Net Business Income</span>
                      <span className="font-semibold">${result.netBusinessIncome.toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="bg-white/10 rounded-xl p-4">
                    <h3 className="font-semibold mb-3">Self-Employment Tax</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-teal-100">Social Security (12.4%)</span>
                        <span>${result.socialSecurityTax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-teal-100">Medicare (2.9%)</span>
                        <span>${result.medicareTax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-white/20 font-semibold">
                        <span>Total SE Tax</span>
                        <span>${result.totalSETax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Deductible SE Tax</span>
                      <span className="font-semibold">-${result.deductibleSETax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Income Tax</span>
                      <span className="font-semibold">${result.incomeTax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    </div>
                  </div>

                  <div className="bg-white/10 rounded-xl p-6">
                    <div className="text-sm text-teal-100 mb-1">Total Tax Liability</div>
                    <div className="text-4xl font-bold mb-2">${result.totalTax.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
                    <div className="text-sm text-teal-100">Effective Rate: {result.effectiveRate.toFixed(2)}%</div>
                  </div>

                  <div className="bg-yellow-500/20 rounded-xl p-6 border-2 border-yellow-400/30">
                    <div className="flex items-center gap-3 mb-2">
                      <i className="ri-calendar-check-line text-2xl text-yellow-300"></i>
                      <div className="text-sm text-yellow-100">Quarterly Payment</div>
                    </div>
                    <div className="text-3xl font-bold">${result.quarterlyPayment.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
                    <p className="text-yellow-100 text-sm mt-2">Due: Apr 15, Jun 15, Sep 15, Jan 15</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center text-teal-100">
                    <i className="ri-calculator-line text-6xl mb-4 opacity-50"></i>
                    <p>Enter your business income to calculate self-employment tax</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Important Information</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Deductible Portion</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    You can deduct 50% of your self-employment tax as an adjustment to income, 
                    reducing your taxable income.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-event-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Quarterly Payments</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Make estimated tax payments quarterly to avoid penalties. Payments are due 
                    April 15, June 15, September 15, and January 15.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-list-3-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Schedule SE</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Report self-employment tax on Schedule SE (Form 1040) when filing your 
                    annual tax return.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Social Security Cap</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    For 2026, Social Security tax applies only to the first $168,600 of net earnings. 
                    Medicare tax has no cap.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}